import React from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from '../components/MarketingHeader';
import { REQUEST_ACCESS_URL } from '../config/links';
import LegalFooter from '../components/LegalFooter';
import useDocumentMeta from '../hooks/useDocumentMeta';

/* Security & trust — backs the "your IP stays yours" promise made on
   /start and the pricing pages, and gives the defense/aerospace audience
   a straight answer about posture. Every claim here must stay consistent
   with the Privacy Policy; nothing is claimed that isn't true today
   (no compliance badges we don't hold). */

const SECTIONS = [
  {
    title: 'Your IP is yours',
    body: 'Your designs, decisions, analysis, and test data are your intellectual property. We host it; we don\'t own it. We do not use your files to train models, build a catalog of parts, or generate any internal asset without your explicit, written permission — this commitment is written into our Privacy Policy.',
  },
  {
    title: 'Private by default',
    body: 'Projects are private. Nothing you design, decide, or test is visible outside the people you authorize on your team. There is no public gallery of your work, and node data never appears in marketing, demos, or other customers\' workspaces.',
  },
  {
    title: 'Data in transit and at rest',
    body: 'All traffic to Factory OS is encrypted in transit over TLS. Data is stored with a small number of vetted infrastructure providers (hosting, database, email delivery, real-time presence), each receiving only what it needs to operate the service and contractually required to protect it.',
  },
  {
    title: 'Access, export, and deletion',
    body: 'You can request a copy of your data, corrections, or deletion at any time via privacy@factory-os.com. We never delete your engineering record as a pricing mechanism — at the free tier\'s 100-node limit your record goes read-only, not away.',
  },
  {
    title: 'Responsible disclosure',
    body: 'If you believe you\'ve found a security issue, email security@factory-os.com with details and steps to reproduce. We ask for a reasonable window to respond and fix before public disclosure, and we\'ll credit researchers who report in good faith.',
  },
  {
    title: 'Enterprise controls',
    body: 'SSO, security reviews, deployment controls, and integrations are available on the Enterprise tier. If your organization has specific hosting, tenancy, or compliance requirements — including regulated defense environments — talk to us early and we\'ll walk through your environment honestly, including what we don\'t support yet.',
  },
  {
    title: 'ITAR-aligned deployment (on request)',
    body: 'For restricted and controlled environments, we offer an ITAR-aligned deployment option: AI inference and embeddings run on models you host inside your own boundary, so controlled technical data never leaves your environment — while your team still works in Factory OS. We\'re building it with design partners; email security@factory-os.com to scope it for your program.',
  },
];

const SecurityTrustPage = () => {

  useDocumentMeta({
    title: 'Security & Trust — Your IP stays yours | Factory OS',
    description:
      'How Factory OS protects your engineering data: your designs and decisions are your IP and are never used to train models; projects are private by default; TLS in transit; export and deletion on request; responsible disclosure; SSO and deployment controls on Enterprise, plus an ITAR-aligned deployment option with customer-hosted AI inference and embeddings on request.',
    path: '/security',
  });

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      <section className="pt-14 sm:pt-20 pb-12 px-5">
        <div className="max-w-[820px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          <div className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>security &amp; trust</div>
          <h1 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            Your engineering record is<br />
            <span className="nb-under">yours. Full stop.</span>
          </h1>
          <p className="mt-4 max-w-[640px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
            You&apos;re trusting Factory OS with the most valuable thing your company produces — the record of how your products became real. Here is exactly how we treat it.
          </p>

          <div className="mt-9 grid grid-cols-1 md:grid-cols-2 gap-3">
            {SECTIONS.map(({ title, body }) => (
              <div key={title} className="nb-sketch-thin p-4">
                <div style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', marginBottom: 6 }}>{title}</div>
                <div style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>{body}</div>
              </div>
            ))}
          </div>

          <div className="nb-sticky mt-8" style={{ transform: 'rotate(-0.4deg)' }}>
            <b>An honest note:</b> Factory OS is an early company. We don&apos;t list compliance badges we don&apos;t hold. If your procurement process needs specific certifications, security reviews, or documentation, email{' '}
            <a href="mailto:security@factory-os.com" style={{ color: 'var(--nb-accent)' }}>security@factory-os.com</a> and we&apos;ll tell you where we are and what&apos;s on the roadmap.
          </div>

          <p className="mt-6" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-3)' }}>
            The full details live in the <a href="/privacy" style={{ color: 'var(--nb-accent)' }}>Privacy Policy</a> and <a href="/terms" style={{ color: 'var(--nb-accent)' }}>Terms of Use</a>.
          </p>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default SecurityTrustPage;
